import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import SuritLogo from "../components/brand/SuritLogo";
import { api } from "../lib/api";

// TEN.4 -- el dueño de una clínica recién pagada crea aquí su contraseña con el enlace del correo.
//
// 🔴 El token viene en el FRAGMENTO (`#t=`), no en la query: el fragmento no sale del navegador,
//    así que no llega a ningún servidor ni a ningún log de acceso. Se lee UNA vez, se BORRA de la
//    barra de direcciones antes de hacer nada más (que no quede en el historial ni en una captura)
//    y viaja en el CUERPO de un POST.
// 🔴 Los textos de error los pone el backend (`{detail, code}`); aquí sólo se decide la acción.
// 🔴 Al terminar NO se inicia sesión (decisión de Jesús): se va al login con el correo puesto.

function leerToken() {
  const m = /[#&]t=([^&]+)/.exec(window.location.hash || "");
  return m ? decodeURIComponent(m[1]) : "";
}

export default function Acceso() {
  const nav = useNavigate();
  // Se lee en el inicializador y NO en el efecto: en desarrollo React ejecuta los efectos dos
  // veces, y la segunda ya encontraría la barra limpia.
  const [token] = useState(leerToken);
  const [estado, setEstado] = useState({ fase: "comprobando" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
    if (!token) {
      setEstado({ fase: "error", detail: "Este enlace no es válido. Ábrelo directamente desde el correo que te enviamos." });
      return;
    }
    api.post("/auth/acceso/estado", { token })
      .then(({ data }) => setEstado({ fase: "listo", nombre: data.nombre }))
      .catch((e) => setEstado({
        fase: "error",
        detail: e.response?.data?.detail || "No pudimos comprobar el enlace. Intenta de nuevo en un momento.",
        code: e.response?.data?.code,
      }));
  }, [token]);

  const enviar = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("La contraseña debe tener al menos 8 caracteres.");
    if (pw !== pw2) return setErr("Las dos contraseñas no coinciden.");
    setEnviando(true);
    try {
      const { data } = await api.post("/auth/acceso", { token, password: pw });
      nav("/login", { replace: true, state: { email: data.email, passwordCreada: true } });
    } catch (e2) {
      const d = e2.response?.data;
      if (d?.code && d.code.startsWith("ACCESO_")) {
        setEstado({ fase: "error", detail: d.detail, code: d.code });
      } else {
        setErr(typeof d?.detail === "string" ? d.detail : "No se pudo guardar tu contraseña. Intenta de nuevo.");
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-8">
        <Link to="/" className="flex items-center w-fit"><SuritLogo size={42} tone="claro" rotulo label="Surit Clínicas" /></Link>

        {estado.fase === "comprobando" && (
          <div className="mt-8 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Comprobando tu enlace…
          </div>
        )}

        {estado.fase === "error" && (
          <div className="mt-6 space-y-4" data-testid="acceso-error">
            <h1 className="font-display text-2xl font-bold">No pudimos usar este enlace</h1>
            <p className="text-sm text-slate-700">{estado.detail}</p>
            {estado.code === "ACCESO_USADO" && (
              <Link to="/login" className="inline-block text-sm font-medium text-blue-700 underline">Ir a iniciar sesión</Link>
            )}
          </div>
        )}

        {estado.fase === "listo" && (
          <form onSubmit={enviar} className="mt-6 space-y-4" data-testid="acceso-form">
            <h1 className="font-display text-2xl font-bold">Crea tu contraseña</h1>
            {estado.nombre && <p className="text-sm text-slate-600">Hola, {estado.nombre}.</p>}
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contraseña</label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="acceso-password" required />
              <p className="text-xs text-slate-500 mt-1">Al menos 8 caracteres.</p>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Repítela</label>
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="acceso-password2" required />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
            <button type="submit" disabled={enviando}
              className="w-full bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700 disabled:opacity-60"
              data-testid="acceso-submit">
              {enviando ? "Guardando…" : "Guardar y continuar"}
            </button>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> El enlace sirve una sola vez.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
